<template>
  <div
    class="re-field"
    :class="props.class"
  >
    <div
      v-if="$slots.label || props.label"
      class="label all-pointer-events row justify-between"
    >
      <slot name="label">
        <p class="row no-wrap">
          {{ props.label !== true ? props.label : '&nbsp;' }}
          <re-tooltip
            v-if="props.tooltip"
            :label="props.tooltip"
          />
        </p>
      </slot>
      <re-icon
        v-if="showWarningIcon"
        class="self-baseline"
        color="warning"
        name="mdi-alert-octagon"
        size="sm"
      />
    </div>
    <q-field
      v-if="!props.noQField"
      ref="fieldTemplateRef"
      borderless
      hide-bottom-space
      :bottom-slots="!!$slots.hint"
      :rules="effectiveRules"
      :no-error-icon="props.noErrorIcon"
      v-bind="$attrs"
      @input="(val) => console.warn('ReField: input event emitted', val)"
    >
      <!-- Passing all other parent slots directly: -->
      <template
        v-for="slotName in Object.keys($slots)"
        :key="slotName"
        #[slotName]="slotProps"
      >
        <slot
          v-if="slotName != 'label'"
          :name="slotName"
          v-bind="slotProps ?? {}"
        />
      </template>
    </q-field>
    <slot
      v-for="slotName in Object.keys($slots).filter((slotName) => slotName !== 'label')"
      v-else
      :key="slotName"
      :name="slotName"
    />
  </div>
</template>
<script setup>
import { computed, ref } from 'vue'

import reRules from '@/plugins/rules'

// This is needed to prevent Quasar from adding the $attrs to the root div instead of the q-field:
defineOptions({ inheritAttrs: false })

const props = defineProps({
  label: {
    type: [String, Boolean],
    default: '',
  },
  tooltip: {
    type: String,
    default: '',
  },
  noQField: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  rules: {
    type: Array,
    default: () => [],
  },
  noErrorIcon: {
    type: Boolean,
    default: false,
  },
  class: {
    type: [String, Object],
    default: undefined,
  },
  showWarningIcon: {
    type: Boolean,
    default: false,
  },
})

const effectiveRules = computed(() => {
  const rules = [...props.rules]
  if (props.required) rules.push(reRules.required)
  return rules
})

const fieldTemplateRef = ref(null)

const validate = async () => {
  return await fieldTemplateRef.value?.validate()
}

defineExpose({
  validate,
})
</script>
<style lang="scss" scoped>
.re-field {
  .label {
    max-height: calc(1.2em + 8px);
    height: calc(1.2em + 8px);

    p {
      font-size: 1em;
      text-wrap: nowrap;
      line-height: 1.2em;
      color: var(--q-secondary);
      font-weight: 500;
    }
  }

  :deep(.q-field__control),
  :deep(.q-field__native) {
    color: inherit;
  }

  // TODO: implement nesting and sizing of components within q-field in a cleaner way to avoid having to do this:
  :deep(.q-field__control-container .q-field__native) {
    padding: 0;
  }

  // Prevent expanding the field vertically when an icon is displayed after contents:
  :deep(.q-field__marginal) {
    height: inherit;
  }

  // :deep(.q-field__bottom) {
  //     position: absolute;
  // }
}
</style>
